import { ComponentRef, Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, fromEvent, merge, of, Subject } from 'rxjs';
import { filter, first, map, mapTo, shareReplay, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ApplicationService } from '../utilities/application/application.service';
import { DataService } from '../form-core/data/data.service';
import { ServiceStepID, ServiceStepModel, StepID, StepModel } from '../form-json.types';
import { State } from '../store';
import { selectStepID } from '../store/router-reducers';
import { selectStepValidity } from '../store/step-validity';
import { ButtonComponent } from './button.component';
import { ButtonModule } from './button.module';
import { Actions, ofType } from '@ngrx/effects';
import { loadingEnd, loadingStart } from '../guards/deactivate/deactivate.guard';
import { backToAction } from '../review/review.component';
import { activateSuccess } from '../guards/activate/activate.guard';

@Injectable({
  providedIn: ButtonModule
})
export class ButtonService implements OnDestroy {

  private hostview!: HTMLDivElement
  private nextButton!: ComponentRef<ButtonComponent>
  private backButton: ComponentRef<ButtonComponent> | undefined
  private destroy$: Subject<any> = new Subject()

  constructor(private app: ApplicationService, private store: Store<State>, private data: DataService, private actions$: Actions) { }

  ngOnDestroy(): void {
    this.destroy()
  }

  create() {
    this.hostview = document.createElement('div')
    this.hostview.setAttribute('data-component-type', 'button')
    document.body.appendChild(this.hostview)
    this.nextButton = this.app.attach(ButtonComponent, this.hostview);
    (this.nextButton.location.nativeElement as HTMLElement).style.order = '2'
    const stepID$ = this.store.select(selectStepID)
    const mouseenter$ = fromEvent(this.nextButton.location.nativeElement, 'mouseenter')
    const mouseleave$ = fromEvent(this.nextButton.location.nativeElement, 'mouseleave')
    const steps = this.data.getSteps()
    const attributes = this.data.getAttributes()
    const index$ = stepID$.pipe(map(step => steps.findIndex(_step => _step.id === step)), shareReplay({ bufferSize: 1, refCount: true }))
    const stepModel$ = stepID$.pipe(map(step => this.data.getModel<StepModel | ServiceStepModel>(this.data.getSteps().find(x => x.id === step) as StepID | ServiceStepID)), shareReplay({ bufferSize: 1, refCount: true }))
    const loading$ = merge(this.actions$.pipe(ofType(loadingStart), mapTo(true)), this.actions$.pipe(ofType(loadingEnd), mapTo(false)), stepID$.pipe(mapTo(false)))
    const combined$ = combineLatest([
      merge(mouseenter$.pipe(mapTo(true)), mouseleave$.pipe(mapTo(false)), stepID$.pipe(mapTo(false))),
      stepID$.pipe(switchMap(stepID => this.store.select(selectStepValidity(stepID as string)))),
      loading$
    ]).pipe(
      withLatestFrom(stepModel$, ([hover, validity, loading], stepModel) => ({ hover, validity, loading, stepModel })),
      shareReplay({ bufferSize: 1, refCount: true })
    )
    let isBackTo: boolean = false
    this.actions$.pipe(
      ofType(backToAction)
    ).pipe(takeUntil(this.destroy$)).subscribe(({step}) => {
      isBackTo = true
      this.actions$.pipe(
        ofType(activateSuccess),
        filter(id => step === '/step/' + id.step),
        first(),
        takeUntil(this.destroy$)
      ).subscribe(() => isBackTo = false)
    })
    const mergeBackToReview$ = merge(
      combined$.pipe(filter(() => !isBackTo), map<Combined, NonBackTo>(value => ({ ...value, type: 'nonBackTo' }))),
      this.actions$.pipe(
        ofType(backToAction),
        map<{ step: string }, BackTo>(value => ({ ...value, type: 'backTo' }))
      )
    ).pipe(
      shareReplay({ refCount: true, bufferSize: 1 })
    )
    this.nextButton.instance.content$ = mergeBackToReview$.pipe(
      map(value => {
        if (value.type === 'nonBackTo') {
          const { hover, validity, loading, stepModel } = value
          if (loading) return stepModel.attributes['button-label-sync'] || attributes['button-label-sync']
          if (validity === 'VALID') return stepModel.attributes['button-label-next'] || attributes['button-label-next']
          if (hover) return stepModel.attributes['button-label-error'] || attributes['button-label-error']
          return stepModel.attributes['button-label-next'] || attributes['button-label-next']
        } else {
          const { step } = value
          const backToStepModel = this.data.getModel<ServiceStepModel | StepModel>(this.data.getSteps().find(({id}) => step === '/step/' + id) as ServiceStepID | StepID)
          return backToStepModel.attributes['button-label-backto'] || attributes['button-label-backto']
        }
      })
    )
    this.nextButton.instance.buttonType$ = mergeBackToReview$.pipe(
      map(value => {
        if (value.type === 'nonBackTo') {
          const { hover, validity, loading } = value
          if (loading) return { next: true, loading: true }
          if (validity === 'VALID') return { next: true, success: true }
          if (hover) return { next: true, info: true }
          return { next: true, normal: true }
        } else {
          return { next: true, info: true }
        }
      })
    )
    this.nextButton.instance.url$ = merge(
      index$.pipe(
        filter(() => !isBackTo),
        map(index => index < steps.length - 1 ? `/step/${steps[index + 1].id}` : '/thankyou'),
      ),
      this.actions$.pipe(
        ofType(backToAction),
        map(({step}) => step)
      )
    )
    stepID$.pipe(
      map(step => steps.findIndex(_step => _step.id === step)),
      takeUntil(this.destroy$)
    ).subscribe(index => {
      if (index === 0 && this.backButton) {
        this.app.destroy(this.backButton)
        this.backButton = undefined
      }
      if (index > 0 && !this.backButton) {
        this.backButton = this.app.attach(ButtonComponent, this.hostview)
        this.backButton.instance.buttonType$ = of({ back: true, normal: true });
        (this.backButton.location.nativeElement as HTMLElement).style.order = '1'
        this.backButton.instance.content$ = stepID$.pipe(map(step => {
          const stepModel = this.data.getModel<StepModel | ServiceStepModel>(this.data.getSteps().find(x => x.id === step) as StepID | ServiceStepID)
          return stepModel.attributes['button-label-back'] || attributes['button-label-back']
        }))
        this.backButton.instance.url$ = stepID$.pipe(map(step => {
          const index = steps.findIndex(_step => _step.id === step)
          return `/step/${steps[index - 1].id}`
        }))
      }
    })
  }

  destroy() {
    if (!this.destroy$.closed) {
      this.destroy$.next('')
      this.destroy$.complete()
    }
    if (this.nextButton) this.app.destroy(this.nextButton)
    if (this.backButton) this.app.destroy(this.backButton)
    document.body.removeChild(this.hostview)
  }
}

interface NonBackTo {
  hover: boolean,
  validity: 'VALID' | 'INVALID' | 'PENDING' | 'DISABLED',
  loading: boolean,
  stepModel: StepModel | ServiceStepModel,
  type: 'nonBackTo'
}

type Combined = Omit<NonBackTo, 'type'>

interface BackTo {
  type: 'backTo',
  step: string
}